import { BillingModel } from '@/modules/billing/entities/billing.enums';
import { BillingService } from '@/modules/billing/sevices/billingService';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import { tryOrCatchMessageError } from '@/utils/tryOrCatchMessageError';
import { useCallback, useEffect, useState } from 'react';

const { REACT_APP_BILLING_MODEL_DETECTION_ENABLED } = process.env;

export interface UseBillingModelProps {
  autoFetch?: boolean;
  initialBillingModel?: BillingModel;
}

export const useBillingModel = (options?: UseBillingModelProps) => {
  const { autoFetch = REACT_APP_BILLING_MODEL_DETECTION_ENABLED === 'true', initialBillingModel } =
    options || {};

  const { company } = useCompany();
  const [billingModel, setBillingModel] = useState<BillingModel | undefined>(initialBillingModel);
  const [loading, setLoading] = useState(true);

  const searchBilling = useCallback(async (companyId: string) => {
    return tryOrCatchMessageError(
      async () => {
        return await new BillingService().getBillingModel(companyId);
      },
      {
        messageErrorDefault:
          'Não foi possível carregar as informações do modelo de faturamento, tente novamente mais tarde.',
        catchError: () => ({ model: BillingModel.FEE_FOR_LIFE }),
        tryBefore: () => setLoading(true),
        tryAfter: () => setLoading(false),
      },
    );
  }, []);

  useEffect(() => {
    if (!company?.id || !autoFetch) {
      return;
    }

    (async () => {
      const { model } = (await searchBilling(company.id)) || {};
      if (!model) {
        return;
      }

      setBillingModel(model);
    })();
  }, [autoFetch, company?.id, searchBilling]);

  return {
    searchBilling,
    companyBillingModel: billingModel,
    isCompanyFeeForService: billingModel
      ? billingModel === BillingModel.FEE_FOR_SERVICE
      : undefined,
    isCompanyFeeForLife: billingModel ? billingModel === BillingModel.FEE_FOR_LIFE : undefined,
    loading,
  };
};
